// ConvoCX unified inbox — API client (CON-845, consumes the P1-T1 API).
// Same-origin contract: the app talks to /api + /ws which the dev proxy and
// the staging nginx both forward to the backend.

import type {
  Conversation,
  ConversationStatus,
  Label,
  LoginResponse,
  MeResponse,
  Message,
  PaginatedResponse,
  TeamMember,
} from './types';

export const TOKEN_STORAGE_KEY = 'convox_token';

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public body: unknown = null,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_STORAGE_KEY) ?? sessionStorage.getItem(TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
}

/** Persist the token. `remember=false` keeps the session in sessionStorage only
 * ("save login" off) so it is cleared when the browser tab closes. */
export function setToken(token: string | null, remember = true): void {
  try {
    if (token) {
      if (remember) localStorage.setItem(TOKEN_STORAGE_KEY, token);
      else sessionStorage.setItem(TOKEN_STORAGE_KEY, token);
    } else {
      localStorage.removeItem(TOKEN_STORAGE_KEY);
      sessionStorage.removeItem(TOKEN_STORAGE_KEY);
    }
  } catch {
    // storage unavailable (private mode) — in-memory only
  }
}

async function request<T>(
  path: string,
  options: RequestInit = {},
  token?: string | null,
): Promise<T> {
  const headers: Record<string, string> = {
    ...((options.headers as Record<string, string>) ?? {}),
  };
  if (options.body) headers['Content-Type'] = 'application/json';
  const authToken = token ?? getToken();
  if (authToken) headers['Authorization'] = `Bearer ${authToken}`;

  let res: Response;
  try {
    res = await fetch(path, { ...options, headers });
  } catch (err) {
    throw new ApiError('Network error. Please check your connection.', 0);
  }

  const body = await res.json().catch(() => null);
  if (!res.ok) {
    throw new ApiError(
      typeof body === 'object' && body && 'error' in body ? String(body.error) : `API error ${res.status}`,
      res.status,
      body,
    );
  }
  return body as T;
}

export const api = {
  // ── Auth ─────────────────────────────────────────────────────────────
  async login(email: string, password: string): Promise<LoginResponse> {
    return request<LoginResponse>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  },

  async register(payload: {
    accountName: string;
    email: string;
    password: string;
    displayName?: string;
  }): Promise<LoginResponse> {
    return request<LoginResponse>('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  /** Current session profile (FR-1.2). Carries `role`, so the shell can
   * restore role-gated navigation after a reload — the JWT payload cannot. */
  async me(): Promise<MeResponse> {
    return request<MeResponse>('/api/auth/me');
  },

  /** Create a new workspace (FR-1.3); the creator becomes its workspace admin. */
  async createWorkspace(name: string): Promise<LoginResponse & { workspace: { id: string; name: string } }> {
    return request<LoginResponse & { workspace: { id: string; name: string } }>('/api/auth/workspaces', {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
  },

  /** SSO start contract (stub until the backend provider handshake lands). */
  async ssoStart(provider: 'google' | 'microsoft' | 'oidc'): Promise<{
    provider: string;
    configured: boolean;
    message: string;
    authUrl: string;
    clientId: string | null;
    redirectUri: string;
    state: string;
  }> {
    return request<{
      provider: string;
      configured: boolean;
      message: string;
      authUrl: string;
      clientId: string | null;
      redirectUri: string;
      state: string;
    }>(`/api/auth/sso/${provider}/start`);
  },

  // ── Conversations ────────────────────────────────────────────────────
  listConversations(params?: {
    search?: string;
    // Multi-select: statuses/channels may be single values or arrays, and the
    // status list may include the UI sentinels 'unassigned'/'assigned-me'.
    status?: ConversationStatus | string[];
    channel_id?: string | string[];
    assigned_to?: string;
    inbox_id?: number;
    starred?: boolean;
    archived?: boolean;
  }): Promise<PaginatedResponse<Conversation>> {
    const qs = new URLSearchParams();
    if (params?.search) qs.set('search', params.search);
    if (params?.status) {
      qs.set('status', (Array.isArray(params.status) ? params.status : [params.status]).join(','));
    }
    if (params?.channel_id) {
      qs.set('channel_id', (Array.isArray(params.channel_id) ? params.channel_id : [params.channel_id]).join(','));
    }
    if (params?.assigned_to) qs.set('assigned_to', params.assigned_to);
    if (params?.inbox_id != null) qs.set('inbox_id', String(params.inbox_id));
    if (params?.starred !== undefined) qs.set('starred', String(params.starred));
    if (params?.archived !== undefined) qs.set('archived', String(params.archived));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return request<PaginatedResponse<Conversation>>(`/api/conversations${suffix}`);
  },

  getConversation(id: string): Promise<{ conversation: Conversation }> {
    return request<{ conversation: Conversation }>(`/api/conversations/${encodeURIComponent(id)}`);
  },

  listMessages(id: string): Promise<PaginatedResponse<Message>> {
    return request<PaginatedResponse<Message>>(`/api/conversations/${encodeURIComponent(id)}/messages`);
  },

  sendMessage(id: string, body: string): Promise<Message> {
    return request<Message>(`/api/conversations/${encodeURIComponent(id)}/messages`, {
      method: 'POST',
      body: JSON.stringify({ body }),
    });
  },

  updateConversation(id: string, patch: {
    status?: ConversationStatus;
    priority?: Conversation['priority'];
    assigned_to?: string | null;
    is_archived?: boolean;
    is_starred?: boolean;
  }): Promise<Conversation> {
    return request<Conversation>(`/api/conversations/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    });
  },

  markRead(id: string): Promise<Conversation> {
    return request<Conversation>(`/api/conversations/${encodeURIComponent(id)}/read`, {
      method: 'POST',
    });
  },

  toggleArchive(id: string): Promise<Conversation> {
    return request<Conversation>(`/api/conversations/${encodeURIComponent(id)}/archive`, {
      method: 'POST',
    });
  },

  toggleStar(id: string): Promise<Conversation> {
    return request<Conversation>(`/api/conversations/${encodeURIComponent(id)}/star`, {
      method: 'POST',
    });
  },

  // ── Labels ───────────────────────────────────────────────────────────
  listLabels(): Promise<{ labels: Label[] }> {
    return request<{ labels: Label[] }>('/api/labels');
  },

  assignLabel(conversationId: string, labelId: string): Promise<{ assigned: boolean }> {
    return request<{ assigned: boolean }>(`/api/labels/assign/${encodeURIComponent(conversationId)}`, {
      method: 'POST',
      body: JSON.stringify({ labelId }),
    });
  },

  removeLabel(labelId: string, conversationId: string): Promise<{ removed: boolean }> {
    return request<{ removed: boolean }>(`/api/labels/${encodeURIComponent(labelId)}/assign/${encodeURIComponent(conversationId)}`, {
      method: 'DELETE',
    });
  },

  // ── Team (assignment) ────────────────────────────────────────────────
  listTeamMembers(): Promise<{ members: TeamMember[] }> {
    return request<{ members: TeamMember[] }>('/api/teams/members');
  },
};